import { useEffect, useRef } from 'react';
import { useMap } from '../hooks';

export interface MarkerProps extends google.maps.MarkerOptions {
    onClick?: (marker: google.maps.Marker) => void;
    onMarker?: (marker: google.maps.Marker) => void;
    onMouseOver?: (event: google.maps.MapMouseEvent) => void;
    onMouseOut?: (event: google.maps.MapMouseEvent) => void;
    data?: any;
}

export const Marker: React.VFC<MarkerProps> = ({
    onClick,
    onMarker,
    onMouseOut,
    onMouseOver,
    data,
    ...options
}: MarkerProps) => {
    const marker = useRef(new google.maps.Marker());
    const { map, markers, clusterer } = useMap();

    useEffect(() => {
        marker.current.setMap(map);
        marker.current.set('data', data);
        markers?.add(marker.current);
        onMarker?.(marker.current);
    }, [map]);

    useEffect(() => {
        if (!clusterer) {
            return;
        }
        clusterer.addMarker(marker.current, true);
    }, [clusterer]);

    useEffect(() => {
        return () => {
            marker.current.setMap(null);
            clusterer?.removeMarker(marker.current);
            markers?.delete(marker.current);
        };
    }, []);

    useEffect(() => {
        marker.current.setOptions(options);
    }, [options]);

    useEffect(() => {
        ['click', 'mouseover', 'mouseout'].forEach((eventName) =>
            google.maps.event.clearListeners(marker.current, eventName)
        );

        if (onClick) {
            marker.current.addListener('click', () => onClick(marker.current));
        }

        if (onMouseOver) {
            marker.current.addListener('mouseover', onMouseOver);
        }

        if (onMouseOut) {
            marker.current.addListener('mouseout', onMouseOut);
        }
    }, [map, onClick, onMouseOut, onMouseOver]);

    return null;
};
